import * as React from "react";

import { observer } from "mobx-react";
import { withStores } from "stores";

import { UitkCard } from "@egds/react-core/cards";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkTable, UitkTableBody, UitkTableRow, UitkTableCell, UitkTableCaption } from "@egds/react-core/tables";
import { UitkText } from "@egds/react-core/text";

import { useLocalization } from "@shared-ui/localization-context";

import { FlightsStructuredTableFlexModuleResult } from "typings/microserviceModels/flights-structured-table-flex-module";
import { getFlightDuration } from "components/utility/FlightsLandingPageUtils";
import PopularOriginAirportsPerAirline from "components/flexComponents/FlightsStructuredTable/views/PopularOriginAirportsPerAirline";
import { FlightsStructuredTableProps } from "./typings";
import { locKeys } from "./l10n";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";

export const FlightsStructuredTable = withStores(
  "context",
  "flexModuleModelStore"
)(
  observer(({ context, templateComponent, flexModuleModelStore }: FlightsStructuredTableProps) => {
    const { id } = templateComponent.metadata;

    const model = flexModuleModelStore.getModel(id) as FlightsStructuredTableFlexModuleResult | null;

    const { formatText } = useLocalization();

    if (!model) {
      return null;
    }

    if (model.view === "popular-origin-airports-per-airline") {
      return <PopularOriginAirportsPerAirline context={context} templateComponent={templateComponent} />;
    }

    const { structuredTableDetails } = model;

    if (!structuredTableDetails) {
      return null;
    }

    return (
      <LazyLoad context={context}>
        <div>
          <div className="FlightsStructuredTable uitk-table-horzoverflow" id={id}>
            <UitkSpacing padding={{ block: "three" }} margin={{ block: "six" }}>
              <UitkCard>
                <div>
                  <UitkTable striped="odd">
                    <UitkTableCaption>
                      <UitkText size={400} weight="bold" theme="emphasis">
                        {model.title
                          ? model.title
                          : formatText(
                              locKeys.title,
                              structuredTableDetails.originAirportName,
                              structuredTableDetails.destAirportName
                            )}
                      </UitkText>
                    </UitkTableCaption>
                    <UitkTableBody>
                      <>
                        {structuredTableDetails.originAirportName && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.originAirportName)}</THCell>
                            <UitkTableCell>{structuredTableDetails.originAirportName}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.originAirportIATACode && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.originAirportIATACode)}</THCell>
                            <UitkTableCell>{structuredTableDetails.originAirportIATACode}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.originCityName && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.originCityName)}</THCell>
                            <UitkTableCell>{structuredTableDetails.originCityName}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.destAirportName && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.destAirportName)}</THCell>
                            <UitkTableCell>{structuredTableDetails.destAirportName}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.destinationAirportIATACode && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.destinationAirportIATACode)}</THCell>
                            <UitkTableCell>{structuredTableDetails.destinationAirportIATACode}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.destCityName && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.destCityName)}</THCell>
                            <UitkTableCell>{structuredTableDetails.destCityName}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.flightsAirlineNames && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.flightsAirlineNames)}</THCell>
                            <UitkTableCell>{structuredTableDetails.flightsAirlineNames}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.noOfAirlinesServing && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.noOfAirlinesServing)}</THCell>
                            <UitkTableCell>{structuredTableDetails.noOfAirlinesServing}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.noOfFlightOptions && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.noOfFlightOptions)}</THCell>
                            <UitkTableCell>{structuredTableDetails.noOfFlightOptions}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.flightsLowestPrice && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.flightsLowestPrice)}</THCell>
                            <UitkTableCell>{structuredTableDetails.flightsLowestPrice}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.popularAirline && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.popularAirline)}</THCell>
                            <UitkTableCell>{structuredTableDetails.popularAirline}</UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.distance && structuredTableDetails.distance.length && (
                          <UitkTableRow>
                            <THCell>{formatText(locKeys.distance)}</THCell>
                            <UitkTableCell>
                              {structuredTableDetails.distance.length} {structuredTableDetails.distance.unit}
                            </UitkTableCell>
                          </UitkTableRow>
                        )}
                        {structuredTableDetails.averageFlightTime &&
                          structuredTableDetails.averageFlightTime.hours &&
                          structuredTableDetails.averageFlightTime.mins && (
                            <UitkTableRow>
                              <THCell>{formatText(locKeys.averageFlightTime)}</THCell>
                              <UitkTableCell>
                                {getFlightDuration(
                                  Number(structuredTableDetails.averageFlightTime.hours),
                                  Number(structuredTableDetails.averageFlightTime.mins)
                                )}
                              </UitkTableCell>
                            </UitkTableRow>
                          )}
                      </>
                    </UitkTableBody>
                  </UitkTable>
                </div>
              </UitkCard>
            </UitkSpacing>
          </div>
        </div>
      </LazyLoad>
    );
  })
);

const THCell: React.FC = ({ children }) => (
  <UitkTableCell scope="row">
    <UitkText weight="bold">{children}</UitkText>
  </UitkTableCell>
);

export default FlightsStructuredTable;
